import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { Webhook, Plus, X, Send, RefreshCw, AlertCircle, Eye, Trash2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { formatDateTime } from "@/lib/dates";

const STATUS_PILL = {
  delivered: { cls: "pill-success", icon: CheckCircle2, label: "Delivered" },
  pending:   { cls: "pill-warning", icon: Clock,        label: "Pending" },
  failed:    { cls: "pill-danger",  icon: XCircle,      label: "Failed" },
};

export default function IntegrationsPage() {
  const [endpoints, setEndpoints] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [supported, setSupported] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const [{ data: eps }, { data: ds }, { data: ev }] = await Promise.all([
        api.get("/integrations/webhooks"),
        api.get("/integrations/webhooks/deliveries?limit=50"),
        api.get("/integrations/webhooks/supported-events"),
      ]);
      setEndpoints(eps || []);
      setDeliveries(ds || []);
      setSupported(ev?.events || []);
    } catch (e) { toast.error("Failed to load webhook config"); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const testFire = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/integrations/webhooks/${id}/test`);
      if (data.ok) toast.success(`Test delivered (HTTP ${data.response_status_code})`);
      else toast.error(`Test failed: ${data.error || `HTTP ${data.response_status_code}`}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Test fire failed"); }
    finally { setBusyId(null); }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete webhook "${name}"? This stops all outbound events to that destination.`)) return;
    try { await api.delete(`/integrations/webhooks/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  const retry = async (deliveryId) => {
    try {
      const { data } = await api.post(`/integrations/webhooks/deliveries/${deliveryId}/retry`);
      if (data.ok) toast.success(`Re-delivered (HTTP ${data.response_status_code})`);
      else toast.error(`Still failing: ${data.error || `HTTP ${data.response_status_code}`}`);
      load();
    } catch (e) { toast.error("Retry failed"); }
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">Outbound webhooks</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Webhook className="w-8 h-8" /> Integrations</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Push training, DBS and leave events to a partner app like <strong>Care Connect</strong>. See <a href="/CARE_CONNECT_INTEGRATION.md" target="_blank" className="underline text-[var(--cc-sage)]" rel="noreferrer">the integration guide</a> for the payload schema.</p>
        </div>
        <button className="btn-primary inline-flex items-center gap-1.5" onClick={() => setShowCreate(true)} data-testid="add-webhook-button">
          <Plus className="w-4 h-4" /> Add webhook endpoint
        </button>
      </div>


      <h2 className="font-display text-2xl font-bold mb-3">Registered endpoints</h2>
      <div className="cc-card overflow-hidden mb-8">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-4 py-2 label-eyebrow">Name</th>
              <th className="px-4 py-2 label-eyebrow">URL</th>
              <th className="px-4 py-2 label-eyebrow">Events</th>
              <th className="px-4 py-2 label-eyebrow">Key</th>
              <th className="px-4 py-2 label-eyebrow">Active</th>
              <th className="px-4 py-2 label-eyebrow"></th>
            </tr>
          </thead>
          <tbody>
            {endpoints.length === 0 && (
              <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="empty-webhooks">No webhook endpoints yet. Click &ldquo;Add&rdquo; to wire up Care Connect.</td></tr>
            )}
            {endpoints.map((ep) => (
              <tr key={ep.id} className="border-t border-[var(--cc-border)]" data-testid={`webhook-row-${ep.id}`}>
                <td className="px-4 py-2 font-medium">{ep.name}</td>
                <td className="px-4 py-2 font-mono text-[11px] truncate max-w-md">{ep.url}</td>
                <td className="px-4 py-2 text-[11px]">{ep.events?.length ?? 0} event{ep.events?.length === 1 ? "" : "s"}</td>
                <td className="px-4 py-2 font-mono text-[11px]">••• {ep.api_key_last4}</td>
                <td className="px-4 py-2">
                  <span className={`pill ${ep.is_active ? "pill-success" : "pill-info"}`}>{ep.is_active ? "Active" : "Disabled"}</span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex gap-1 justify-end">
                    <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => testFire(ep.id)} disabled={busyId === ep.id || !ep.is_active} data-testid={`test-webhook-${ep.id}`}>
                      <Send className="w-3 h-3" /> Test
                    </button>
                    <button className="btn-ghost text-xs" onClick={() => setEditing(ep)} data-testid={`edit-webhook-${ep.id}`}>Edit</button>
                    <button className="btn-ghost text-xs text-[var(--cc-accent)]" onClick={() => remove(ep.id, ep.name)} data-testid={`delete-webhook-${ep.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-2xl font-bold mb-3">Recent deliveries</h2>
      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-4 py-2 label-eyebrow">When</th>
              <th className="px-4 py-2 label-eyebrow">Endpoint</th>
              <th className="px-4 py-2 label-eyebrow">Event</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              <th className="px-4 py-2 label-eyebrow">HTTP</th>
              <th className="px-4 py-2 label-eyebrow">Attempts</th>
              <th className="px-4 py-2 label-eyebrow"></th>
            </tr>
          </thead>
          <tbody>
            {deliveries.length === 0 && (
              <tr><td colSpan="7" className="text-center py-8 text-[var(--cc-text-soft)]">No deliveries yet.</td></tr>
            )}
            {deliveries.map((d) => {
              const sp = STATUS_PILL[d.status] || STATUS_PILL.pending;
              const Icon = sp.icon;
              return (
                <tr key={d.id} className="border-t border-[var(--cc-border)]" data-testid={`delivery-row-${d.id}`}>
                  <td className="px-4 py-2 text-xs">{formatDateTime(d.created_at)}</td>
                  <td className="px-4 py-2 text-xs">{d.endpoint_name}</td>
                  <td className="px-4 py-2 text-xs font-mono">{d.event}</td>
                  <td className="px-4 py-2">
                    <span className={`pill ${sp.cls} inline-flex items-center gap-1`}><Icon className="w-3 h-3" /> {sp.label}</span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{d.last_response_status ?? "—"}</td>
                  <td className="px-4 py-2 text-xs">{d.attempts}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1 justify-end">
                      <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => setViewing(d)} data-testid={`view-delivery-${d.id}`}>
                        <Eye className="w-3 h-3" /> View
                      </button>
                      {d.status !== "delivered" && (
                        <button className="btn-ghost text-xs inline-flex items-center gap-1" onClick={() => retry(d.id)} data-testid={`retry-delivery-${d.id}`}>
                          <RefreshCw className="w-3 h-3" /> Retry
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && <EndpointModal supported={supported} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editing && <EndpointModal supported={supported} endpoint={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && <DeliveryModal delivery={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function EndpointModal({ supported, endpoint, onClose, onSaved }) {
  const isEdit = !!endpoint;
  const [form, setForm] = useState({
    name: endpoint?.name || "",
    url: endpoint?.url || "",
    api_key: "",
    events: endpoint?.events || [],
    is_active: endpoint?.is_active ?? true,
    description: endpoint?.description || "",
  });
  const [busy, setBusy] = useState(false);

  const toggle = (e) => setForm({...form, events: form.events.includes(e) ? form.events.filter(x => x !== e) : [...form.events, e]});

  const submit = async () => {
    if (!form.name || !form.url) return toast.error("Name and URL are required");
    if (!isEdit && (!form.api_key || form.api_key.length < 8)) return toast.error("API key must be ≥ 8 characters");
    if (form.events.length === 0) return toast.error("Pick at least one event");
    setBusy(true);
    try {
      const payload = { ...form };
      if (isEdit && !form.api_key) delete payload.api_key; // don't overwrite on edit
      if (isEdit) await api.patch(`/integrations/webhooks/${endpoint.id}`, payload);
      else        await api.post("/integrations/webhooks", payload);
      toast.success(isEdit ? "Endpoint updated" : "Endpoint registered");
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ") : (d || "Save failed"));
    }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} widthClass="max-w-xl" testId="webhook-endpoint-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold">{isEdit ? "Edit endpoint" : "Register webhook endpoint"}</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="label-eyebrow block mb-1">Display name *</label>
          <input className="cc-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Care Connect Production" data-testid="webhook-name-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Receiver URL *</label>
          <input className="cc-input font-mono text-xs" value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} placeholder="https://care-connect.example.com/api/webhooks/recruitment" data-testid="webhook-url-input" />
          <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">Care Connect&apos;s POST endpoint that will receive each event.</div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">API key {isEdit ? "(leave blank to keep current)" : "*"}</label>
          <input className="cc-input font-mono text-xs" type="password" value={form.api_key} onChange={(e) => setForm({...form, api_key: e.target.value})} placeholder={isEdit ? `••• ${endpoint.api_key_last4}` : "Min 8 chars"} data-testid="webhook-key-input" />
          <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">Care Connect should reject any incoming request whose <code>X-API-Key</code> header doesn&apos;t match this value.</div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Subscribed events</label>
          <div className="grid grid-cols-2 gap-1 cc-card p-2 max-h-48 overflow-y-auto">
            {supported.map((e) => (
              <label key={e} className="text-xs cursor-pointer flex items-center gap-1.5 hover:bg-[var(--cc-surface)] px-1.5 py-1 rounded">
                <input type="checkbox" checked={form.events.includes(e)} onChange={() => toggle(e)} data-testid={`event-toggle-${e}`} />
                <code>{e}</code>
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})} data-testid="webhook-active-toggle" /> Active
        </label>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="webhook-save-button">{busy ? "Saving…" : (isEdit ? "Save" : "Register")}</button>
      </div>
    </Modal>
  );
}

function DeliveryModal({ delivery, onClose }) {
  return (
    <Modal onClose={onClose} widthClass="max-w-3xl" testId="webhook-delivery-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold">{delivery.event}</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
        <Field label="Status" value={delivery.status} />
        <Field label="HTTP" value={delivery.last_response_status ?? "—"} />
        <Field label="Attempts" value={delivery.attempts} />
        <Field label="Endpoint" value={delivery.endpoint_name} />
        <Field label="Created" value={formatDateTime(delivery.created_at)} />
        <Field label="Delivered" value={delivery.delivered_at ? formatDateTime(delivery.delivered_at) : "—"} />
      </div>
      {delivery.last_error && (
        <div className="cc-card p-3 mb-3 bg-[#fde8e3] text-xs font-mono whitespace-pre-wrap break-all">{delivery.last_error}</div>
      )}
      <div className="label-eyebrow mb-1">Payload</div>
      <pre className="cc-card p-3 text-[11px] font-mono whitespace-pre-wrap break-all max-h-96 overflow-y-auto">{JSON.stringify({event: delivery.event, delivery_id: delivery.id, occurred_at: delivery.created_at, data: delivery.payload}, null, 2)}</pre>
    </Modal>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow text-[10px] mb-0.5">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
